/**
 * ai/promptBuilder.ts
 *
 * Constructs the AI prompt from MarketContext.
 * Engineered for precision — gives AI exactly what it needs
 * and enforces strict JSON output via the response schema.
 */

import { MarketContext } from '../context/contextBuilder';
import type { FlowSignal } from '../../institutionalFlow/schemas';
import type { ConfluenceSignal } from '../../confluence/schemas';
import type { PrintClusterSnapshot } from '../../institutionalFlow/clusters';
import { computeConfluence } from '../../confluence/engine';
import { SessionPhase } from '../../../utils/session';
import { getScannerContext } from '../../../hooks/useScannerBridge';
import type { GammaUnifiedContext } from '../../market/engines/gamma/gammaInfluenceTypes';
import { gammaToThesisLines } from '../../market/engines/gamma/gammaContextMapper';

// ─── Session phase descriptions ───────────────────────────────────────────────

const PHASE_DESC: Record<SessionPhase, string> = {
  'pre-market':  'Pre-market session. Institutional positioning begins. Watch for gap fills and overnight level tests.',
  'opening':     'Opening 0-15 min. Highest volatility. Wait for opening range to form. False moves common.',
  'first-hour':  'First hour 15-90 min. Primary daily direction often set here. High participation.',
  'mid-morning': 'Mid-morning 90-120 min. Continuation or reversal of opening range thesis. Watch VWAP.',
  'midday':      'Midday 90min-2:30pm ET. Institutional flow dries up. Higher noise, lower conviction setups.',
  'afternoon':   'Afternoon 2:30-3pm ET. Positioning ahead of power hour. Watch for trend resumption.',
  'power-hour':  'Power hour 3-4pm ET. Institutional rebalancing resumes. Strong directional moves possible.',
  'after-hours': 'After hours. Reduced liquidity. Earnings reactions and overnight positioning.',
};

// ─── Phase 3: trader horizon directives ───────────────────────────────────────
//
// Inserted near the top of the prompt so the AI sizes its thesis to the
// trader's intent. See PHASE_3_TIMEFRAME_DESIGN.md §6.

const HORIZON_DIRECTIVES: Record<'scalp' | 'intraday' | 'swing' | 'position', string> = {
  scalp:
    'Trader is positioning for SCALP holds (seconds to minutes). Propose entries with very tight invalidations and short profit targets that fit minute-scale moves. Avoid setups requiring multi-bar waits.',
  intraday:
    'Trader is positioning for INTRADAY holds (minutes to hours, close by EOD). Propose entries that can resolve within the trading session. Avoid swing-style setups requiring overnight or multi-day commitment.',
  swing:
    'Trader is positioning for SWING holds (days). Propose entries, invalidations, and targets that fit days-long holds. Do NOT propose minute-scale tactical entries. Multi-bar waits and broader stops are appropriate. NOTE: backend bar-interval support for >15m bars is pending — operate on 15m bars as a fallback and acknowledge the limitation in `uncertainty`.',
  position:
    'Trader is positioning for POSITION holds (weeks+). Propose entries based on multi-day structural levels. Daily and weekly context dominate. Intraday noise is irrelevant unless it touches a structural level. NOTE: backend bar-interval support for >15m bars is pending — operate on 15m bars as a fallback and acknowledge the limitation in `uncertainty`.',
};

// --- Phase 4: primary setup binding (deterministic alignment anchor) ---
//
// Replaces the Phase 3 ALIGNMENT_DIRECTIVE which asked the AI to interpret a
// 50% confidence threshold itself. We now pre-select the primary setup in code
// and present it authoritatively. See userMemories May 5 for context.
//
// --- ChatGPT review followup: rule on prompt language strength ---
//
// Do NOT further escalate "MUST / NEVER / FORBIDDEN" language without
// empirical evidence of harm from production divergence data.
//
// Architecture:
//   - Prompt alignment = best-effort soft pressure
//   - Deterministic divergence detection (src/domains/divergence/) = source of truth
//
// Re-evaluate this language only if production divergence data shows clear need.
// LLMs do not obey instructions deterministically; escalating wording is a
// fruitless path. Trust the divergence layer to catch what prompt fails to prevent.

function buildPrimarySetupBlock(setups: MarketContext['topSetups']): string {
  const primary = setups[0];
  if (!primary) {
    return 'PRIMARY SETUP\nNo deterministic setup available. Generate the thesis using market structure, liquidity, regime, and gamma context.';
  }

  // Surface the numerical levels (entryPrice, stopPrice, target1Price)
  // as distinct prompt lines alongside the descriptive text. The AI was
  // observed collapsing both "entry" and "invalidation" to the same
  // structural level (e.g. the BOS price) because the text strings for
  // both reference the same trigger anchor. The numerical fields, when
  // populated, are the authoritative levels.
  const entryPriceLine =
    primary.entryPrice != null
      ? `Entry price (target — use this as the numerical entry): ${primary.entryPrice.toFixed(2)}`
      : 'Entry price: unavailable — use the trigger text level';
  const stopPriceLine =
    primary.stopPrice != null
      ? `Stop level (price — use this as the numerical invalidation): ${primary.stopPrice.toFixed(2)}`
      : 'Stop level: unavailable — use the invalidation text level';
  const t1Line =
    primary.target1Price != null
      ? `T1 (numerical): ${primary.target1Price.toFixed(2)}`
      : `T1: ${primary.t1 || 'not set'}`;
  const t2Line =
    primary.target2Price != null
      ? `T2 (numerical): ${primary.target2Price.toFixed(2)}`
      : (primary.t2 ? `T2: ${primary.t2}` : '');

  return `PRIMARY SETUP (you MUST anchor your thesis to this)
Type: ${primary.type}
Direction: ${primary.dir.toUpperCase()}
Confidence: ${primary.confidence}%
Entry trigger (describes the event): ${primary.entry}
${entryPriceLine}
Invalidation trigger (describes the event): ${primary.invalidation}
${stopPriceLine}
${t1Line}${t2Line ? `\n${t2Line}` : ''}

Numerical-vs-text discipline:
The trigger text describes WHAT must happen (e.g. "Pullback to BOS level
701.14") and references the structural anchor. The numerical entry/stop
lines are the AUTHORITATIVE PRICE LEVELS. These three values
(structural anchor, entry price, stop level) are distinct by design and
should NOT collapse to the same number in your output. For example, a
BOS Long Continuation setup has a structural BOS level (the trigger
anchor), an entry price (typically the current pullback price), and a
stop level (typically a small offset beyond the BOS). When you write
your entry and invalidation fields, use the NUMERICAL price lines
above; do not extract the structural anchor from the trigger text.

Your AI Thesis direction, entryLogic, invalidation, and targetLogic MUST
align with the PRIMARY SETUP within 0.5%. Do not invent alternate
levels. If you have explicit reason to override (e.g. setup confidence
too low, contradictory market structure), you MUST state the override
reason in the \`uncertainty\` field.`;
}

// ─── Prior thesis context ─────────────────────────────────────────────────────

/**
 * Render the PRIOR ANALYSIS block. Now passes through richer prior-thesis
 * context (actionability, bestSetupType, shouldTradeNow) so the AI can
 * judge thesisChange against an actionability trajectory, not just a
 * narrative comparison.
 *
 * Pre-Phase-2 callers passed only `priorThesis` (narrative text) and
 * `priorConviction`. The new fields are optional and the rendering
 * gracefully falls back when they're missing.
 */
function formatPriorThesis(
  priorThesis:        string | null,
  priorConviction:    number | null,
  priorActionability: number | null = null,
  priorSetupType:     string | null = null,
  priorShouldTrade:   boolean | null = null,
): string {
  if (!priorThesis) return 'No prior thesis — this is a fresh analysis.';
  // Optional structured fields render on a second line when present so
  // they don't disrupt prompts whose orchestrators haven't been updated yet.
  const structuredParts: string[] = [];
  if (priorActionability != null) structuredParts.push(`actionability ${priorActionability}`);
  if (priorSetupType)             structuredParts.push(`setup ${priorSetupType}`);
  if (priorShouldTrade != null)   structuredParts.push(`shouldTradeNow ${priorShouldTrade}`);
  const structuredLine = structuredParts.length
    ? `\nPrior state: ${structuredParts.join(' | ')}`
    : '';
  return `Prior thesis (conviction ${priorConviction ?? '?'}%): "${priorThesis}"${structuredLine}
Compare the current signals against this prior thesis and explicitly classify the change.`;
}

// ─── Main prompt builder ──────────────────────────────────────────────────────

/**
 * Phase 6.2: Live Gamma context from the server-side snapshot.
 * Passed as optional informational context only — does NOT influence scoring.
 * If absent, thesis generation continues normally without it.
 */

// ─── In-process live gamma store (mirrors scanner bridge pattern) ─────────────
// ThesisPanel writes here after each snapshot fetch; cleared on symbol change.
// buildAIPrompt reads here when constructing the prompt.
// Gamma unavailability never blocks thesis generation.
// Bounded: store is capped at MAX_GAMMA_STORE_SIZE entries.
const _liveGammaStore = new Map<string, GammaUnifiedContext>();
const MAX_GAMMA_STORE_SIZE = 20;

// ── Institutional flow sidecar (Phase 1 v1 integration) ───────────────────
// FlowSignal store: mirrors the gamma pattern. Updated by useFlowSignal each
// poll; consumed inside buildAIPrompt to inject institutional flow context.
// Bounded same as gamma store -- 20 symbols, oldest evicted.
const _liveFlowStore = new Map<string, FlowSignal>();
const MAX_FLOW_STORE_SIZE = 20;

export function setLiveFlowContext(symbol: string, signal: FlowSignal): void {
  const key = symbol.toUpperCase();
  _liveFlowStore.set(key, signal);
  if (_liveFlowStore.size > MAX_FLOW_STORE_SIZE) {
    const oldest = _liveFlowStore.keys().next().value;
    if (oldest && oldest !== key) _liveFlowStore.delete(oldest);
  }
  _emitFlowChange(key);
}

export function getLiveFlowContext(symbol: string): FlowSignal | null {
  return _liveFlowStore.get(symbol.toUpperCase()) ?? null;
}

/** Removes a symbol's entry. Called on symbol change / runtime cleanup. */
export function clearLiveFlowContext(symbol: string): void {
  const key = symbol.toUpperCase();
  _liveFlowStore.delete(key);
  _emitFlowChange(key);
}

// ── Flow sidecar subscription (Phase 1 hardening) ─────────────────────────
//
// Display surfaces (FlowDebugPanel) read from the sidecar via
// subscribeFlowContext so they re-render when the runtime updates the
// signal. The AI prompt path still uses getLiveFlowContext synchronously
// at build time -- no change.
//
// The listener is invoked with the upper-cased symbol on every set or
// clear. Subscribers should call getLiveFlowContext(symbol) to read the
// current value.

type FlowListener = (symbol: string) => void;
const _flowListeners = new Set<FlowListener>();

function _emitFlowChange(symbol: string): void {
  for (const l of _flowListeners) {
    try { l(symbol); } catch { /* swallow listener errors */ }
  }
}

/**
 * Subscribe to flow sidecar updates. Returns an unsubscribe function.
 * Invoked with the upper-cased symbol whenever setLiveFlowContext or
 * clearLiveFlowContext mutates that key.
 */
export function subscribeFlowContext(listener: FlowListener): () => void {
  _flowListeners.add(listener);
  return () => { _flowListeners.delete(listener); };
}

// ── Confluence sidecar (Phase 2 v1) ────────────────────────────────────────
//
// Mirrors the flow sidecar pattern. computeConfluence is called inside
// buildAIPrompt; the result is stored here so future UI panels can
// subscribe without re-running the engine. AI prompt path uses the
// in-scope signal directly, not getLiveConfluenceContext.
//
// Bounded same as gamma/flow stores -- 20 symbols, oldest evicted.

const _liveConfluenceStore = new Map<string, ConfluenceSignal>();
const MAX_CONFLUENCE_STORE_SIZE = 20;

export function setLiveConfluenceContext(symbol: string, signal: ConfluenceSignal): void {
  const key = symbol.toUpperCase();
  _liveConfluenceStore.set(key, signal);
  if (_liveConfluenceStore.size > MAX_CONFLUENCE_STORE_SIZE) {
    const oldest = _liveConfluenceStore.keys().next().value;
    if (oldest && oldest !== key) _liveConfluenceStore.delete(oldest);
  }
  _emitConfluenceChange(key);
}

export function getLiveConfluenceContext(symbol: string): ConfluenceSignal | null {
  return _liveConfluenceStore.get(symbol.toUpperCase()) ?? null;
}

export function clearLiveConfluenceContext(symbol: string): void {
  const key = symbol.toUpperCase();
  _liveConfluenceStore.delete(key);
  _emitConfluenceChange(key);
}

type ConfluenceListener = (symbol: string) => void;
const _confluenceListeners = new Set<ConfluenceListener>();

function _emitConfluenceChange(symbol: string): void {
  for (const l of _confluenceListeners) {
    try { l(symbol); } catch { /* swallow listener errors */ }
  }
}

/**
 * Subscribe to confluence sidecar updates. Returns an unsubscribe
 * function. Invoked with the upper-cased symbol whenever
 * setLiveConfluenceContext or clearLiveConfluenceContext mutates that
 * key.
 */
export function subscribeConfluenceContext(listener: ConfluenceListener): () => void {
  _confluenceListeners.add(listener);
  return () => { _confluenceListeners.delete(listener); };
}

// ── Print-clusters sidecar (Phase 2 v1 -- liquidity multi-source) ──────────
//
// Mirrors the flow / confluence sidecar pattern. The institutional flow
// runtime computes off-exchange print clusters from the same Trade[] used
// by computeFlow and stores the snapshot here. liquidityEngine reads from
// this sidecar (via the symbol passed through buildSymbolContext) to boost
// existing zones that have dark-pool support.
//
// Cluster data is interpretive, not raw. The runtime owns clustering;
// downstream consumers read aggregated clusters by price level and decide
// how to weight them. liquidityEngine in v1 uses them as confirmation
// only -- never to generate orphan zones.
//
// Bounded same as gamma/flow/confluence stores -- 20 symbols, oldest evicted.

const _livePrintClustersStore = new Map<string, PrintClusterSnapshot>();
const MAX_PRINT_CLUSTERS_STORE_SIZE = 20;

export function setLivePrintClusters(symbol: string, snapshot: PrintClusterSnapshot): void {
  const key = symbol.toUpperCase();
  _livePrintClustersStore.set(key, snapshot);
  if (_livePrintClustersStore.size > MAX_PRINT_CLUSTERS_STORE_SIZE) {
    const oldest = _livePrintClustersStore.keys().next().value;
    if (oldest && oldest !== key) _livePrintClustersStore.delete(oldest);
  }
  _emitPrintClustersChange(key);
}

export function getLivePrintClusters(symbol: string): PrintClusterSnapshot | null {
  return _livePrintClustersStore.get(symbol.toUpperCase()) ?? null;
}

export function clearLivePrintClusters(symbol: string): void {
  const key = symbol.toUpperCase();
  _livePrintClustersStore.delete(key);
  _emitPrintClustersChange(key);
}

type PrintClustersListener = (symbol: string) => void;
const _printClustersListeners = new Set<PrintClustersListener>();

function _emitPrintClustersChange(symbol: string): void {
  for (const l of _printClustersListeners) {
    try { l(symbol); } catch { /* swallow listener errors */ }
  }
}

/**
 * Subscribe to print-clusters sidecar updates. Returns an unsubscribe
 * function. Invoked with the upper-cased symbol whenever
 * setLivePrintClusters or clearLivePrintClusters mutates that key.
 */
export function subscribePrintClusters(listener: PrintClustersListener): () => void {
  _printClustersListeners.add(listener);
  return () => { _printClustersListeners.delete(listener); };
}


export function setLiveGammaContext(symbol: string, ctx: GammaUnifiedContext): void {
  const key = symbol.toUpperCase();
  _liveGammaStore.set(key, ctx);
  if (_liveGammaStore.size > MAX_GAMMA_STORE_SIZE) {
    const oldest = _liveGammaStore.keys().next().value;
    if (oldest && oldest !== key) _liveGammaStore.delete(oldest);
  }
}

export function getLiveGammaContext(symbol: string): GammaUnifiedContext | null {
  return _liveGammaStore.get(symbol.toUpperCase()) ?? null;
}

/** Removes a symbol's entry. Called on symbol change / component unmount in ThesisPanel. */
export function clearLiveGammaContext(symbol: string): void {
  _liveGammaStore.delete(symbol.toUpperCase());
}

// Keep LiveGammaContext as a deprecated alias so external callers compile unchanged
/** @deprecated Use GammaUnifiedContext from gammaInfluenceTypes.ts */
export type LiveGammaContext = GammaUnifiedContext;

export function buildAIPrompt(
  ctx:              MarketContext,
  priorThesis:      string | null,
  priorConviction:  number | null,
  // Phase 3: optional trader horizon. When provided, AI is told to size its
  // thesis to this horizon and to align with deterministic setups at this
  // horizon. Defaults to 'intraday' (matches DEFAULT_HORIZON in shared/types)
  // when caller hasn't been wired up yet.
  traderHorizon?:   'scalp' | 'intraday' | 'swing' | 'position',
  // Phase 2 prompt fix (thesisChange semantics): additional prior-thesis
  // context so the AI can classify the change against an actionability
  // trajectory, not just a narrative compare. All optional and default to
  // null so existing callers compile unchanged.
  priorActionability?: number | null,
  priorSetupType?:     string | null,
  priorShouldTrade?:   boolean | null,
): string {
  const horizon = traderHorizon ?? 'intraday';
  const { symbol, raw, regime, gamma, smc, topZones, topSetups, sessionPhase } = ctx;
  const r = raw;

  // Format zones. Phase 2 v1 (liquidity multi-source): zones confirmed by
  // off-exchange print clusters are marked with [DP] so the AI can weight
  // dark-pool-supported levels accordingly.
  const zoneStr = topZones.length
    ? topZones.map(z => {
        const ze = z as typeof z & { darkPoolSupport?: boolean; darkPoolPrintCount?: number; darkPoolShares?: number };
        const dp = ze.darkPoolSupport
          ? ` [DP: ${ze.darkPoolPrintCount} prints / ${(ze.darkPoolShares ?? 0).toLocaleString()} shares]`
          : '';
        return `  ${z.type.toUpperCase()} ${z.price.toFixed(2)} [${z.sig}]${dp} — ${z.label} (${z.distPct.toFixed(2)}% away)`;
      }).join('\n')
    : '  No high-significance zones detected';

  // Format traps
  const trapStr = ctx.activeTraps.length
    ? ctx.activeTraps.map(t => `  [${t.severity}] ${t.type}${t.level ? ` @ ${t.level.toFixed(2)}` : ''}: ${t.reason}`).join('\n')
    : '  No active trap signals';

  // Format setups — include maturity/alignment/confirmation state (v5.5 false-confluence patch)
  const setupStr = topSetups.length
    ? topSetups.map((s, i) => {
        const maturity   = s.maturity   ?? 'unknown';
        const alignment  = s.alignment  ?? 'unknown';
        const missing    = (s.confirmationMissing?.length ?? 0)
          ? `\n     Missing: ${s.confirmationMissing!.join(', ')}`
          : '';
        const countertrend  = s.countertrend ? '\n     Countertrend: YES' : '';
        const transition    = `\n     Transition Confirmed: ${s.stateTransitionConfirmed ? 'YES' : 'NO'}`;
        return `  ${i + 1}. ${s.type} (${s.dir.toUpperCase()}, conf ${s.confidence}%)\n     Maturity: ${maturity}\n     Alignment: ${alignment}${countertrend}${transition}\n     Entry: ${s.entry}\n     Inv: ${s.invalidation}\n     T1: ${s.t1}\n     T2: ${s.t2}${missing}`;
      }).join('\n')
    : '  No high-confidence setups currently';

  // Format SMC
  const smcStr = smc
    ? `Bias: ${smc.bias} (${smc.biasStrength}) | Key signal: ${smc.keySignal}
  BOS: ${smc.lastBOS ? `${smc.lastBOS.dir} @ ${smc.lastBOS.price.toFixed(2)}` : 'none'}
  CHOCH: ${smc.lastCHOCH ? `${smc.lastCHOCH.dir} @ ${smc.lastCHOCH.price.toFixed(2)}` : 'none'}
  Sweep+Reclaim: ${smc.sweepAndReverse ? 'YES — reversal evidence present, confirmation required unless setup is actionable' : 'no'}
  Displacement: ${smc.recentDisplacement ? `${smc.recentDisplacement.dir} (${smc.recentDisplacement.volumeMultiple.toFixed(1)}x vol)` : 'none'}
  Premium/Discount: ${smc.premiumDiscount?.zone || 'unknown'} (${smc.premiumDiscount?.positionPct.toFixed(0) || '?'}% of range)
  FVG below: ${smc.fvgBelow ? `${smc.fvgBelow.bottom.toFixed(2)}-${smc.fvgBelow.top.toFixed(2)}` : 'none'}
  FVG above: ${smc.fvgAbove ? `${smc.fvgAbove.bottom.toFixed(2)}-${smc.fvgAbove.top.toFixed(2)}` : 'none'}
  OB below: ${smc.obBelow ? `${smc.obBelow.bottom.toFixed(2)}-${smc.obBelow.top.toFixed(2)}` : 'none'}
  OB above: ${smc.obAbove ? `${smc.obAbove.bottom.toFixed(2)}-${smc.obAbove.top.toFixed(2)}` : 'none'}`
    : 'No SMC data available';

  // Format gamma — combines CSV-based (existing) with Phase 6.1 live snapshot (optional)
  // Live gamma is informational context only. It does not override structure/regime/SMC.
  const gammaStr = (() => {
    const csvPart = gamma
      ? `CSV-OI: Regime ${gamma.regime} | Flip ${gamma.gammaFlip?.toFixed(2) || '?'} | Call Wall ${gamma.callWall?.toFixed(2) || '?'} | Put Wall ${gamma.putWall?.toFixed(2) || '?'} | Vol Risk ${gamma.volRisk} | P/C ${gamma.pcRatio?.toFixed(2) || '?'}`
      : null;

    const liveGammaCtx = getLiveGammaContext(symbol);
    const livePart = liveGammaCtx ? gammaToThesisLines(liveGammaCtx) : null;

    if (csvPart && livePart) return `${csvPart}\n${livePart}`;
    if (livePart) return livePart;
    if (csvPart) return csvPart;
    return 'No options data — gamma analysis unavailable';
  })();

  // Volatility context — optional; sourced from server-side VIX fetch via /api/volatility/context
  // Injected as interpretive context; does not alter structural gamma/wall/flip math.
  const volCtx = (ctx as any).volContext as import('../../market/engines/volatility/volatilityInterpreter').VolatilityContext | null | undefined;
  const volStr = volCtx
    ? `Vol regime: ${volCtx.regime}${volCtx.vixLevel !== null ? ` (VIX ${volCtx.vixLevel.toFixed(2)})` : ''} | trapMod=×${volCtx.modifiers.trapRiskModifier} confMod=×${volCtx.modifiers.confidenceModifier}`
    : null;
  const volContextLine = volStr
    ? `\nVol context: ${volStr}${volCtx?.reasoning?.[1] ? ' | ' + volCtx.reasoning[1] : ''}`
    : '';

  // Institutional flow context (Phase 1 v1 integration). Sidecar-stored by
  // useFlowSignal. Null when the FlowDebugPanel hasn't been opened for this
  // symbol or markets are closed -- we still emit a section so the AI knows
  // this signal class exists and isn't currently informative.
  const flowSig = getLiveFlowContext(symbol);
  const flowStr = flowSig
    ? (() => {
        const m = flowSig.metrics;
        const biasLabel = flowSig.bias.replace(/_/g, ' ').toUpperCase();
        const evidenceLines = flowSig.evidence.map(e => `  - ${e}`).join('\n');
        return `Signal: ${biasLabel} | confidence ${(flowSig.confidence * 100).toFixed(0)}%
Off-exchange: ${(m.offExchangePct * 100).toFixed(1)}% (${m.offExchangeVolume.toLocaleString()} / ${m.totalVolume.toLocaleString()} shares) | Blocks: ${m.blockCount} (largest ${m.largestBlock.toLocaleString()})
VWAP: ${m.vwap.toFixed(2)} | Last: ${m.lastPrice.toFixed(2)} (${m.vwapDelta >= 0 ? '+' : ''}${(m.vwapDelta * 100).toFixed(2)}%)
Evidence:
${evidenceLines}
Treat institutional flow as confirmation or contradiction context. It does NOT override structure/regime/SMC, but when it agrees with structural bias it strengthens conviction; when it contradicts, raise the uncertainty field.`;
      })()
    : 'Institutional flow data not available this cycle (markets closed or initial fetch pending)';

  // Phase 2 v1: compute deterministic confluence across the 5 directional
  // engines. Pure transformation of in-scope context + sidecar flow signal.
  // Result is stored in the sidecar so future UI surfaces can subscribe
  // without re-running the engine.
  const confluence = computeConfluence(symbol, ctx, flowSig);
  setLiveConfluenceContext(symbol, confluence);

  const confluenceStr = (() => {
    const biasLabel = confluence.bias.toUpperCase();
    const scoreStr  = confluence.score >= 0 ? `+${confluence.score.toFixed(2)}` : confluence.score.toFixed(2);
    const evidenceBody = confluence.evidence.map(e => e.startsWith('  ') ? e : `- ${e}`).join('\n');
    return `Signal: ${biasLabel} | score ${scoreStr} | confidence ${(confluence.confidence * 100).toFixed(0)}%
${evidenceBody}

Confluence agrees with structure or contradicts it. It does NOT override deterministic setups; treat it as one input alongside the PRIMARY SETUP block below. When confluence and the primary setup direction disagree, that disagreement is meaningful uncertainty -- name it in the \`uncertainty\` field.`;
  })();


  // Format regime. Phase 2 v1: when a regime modifier fires, render a
  // Modifier line right under Posture so the AI sees the macro state
  // and the live micro-condition in the same block. The line is omitted
  // entirely when no modifier qualifies (modifier == null), keeping the
  // prompt format identical to pre-v1 in the common case.
  const modifierLine = regime?.modifier
    ? `\n  Modifier: ${regime.modifier.type.toUpperCase()} (${regime.modifier.severity}, conf ${(regime.modifier.confidence * 100).toFixed(0)}%) -- ${regime.modifier.reason}`
    : '';
  const regimeStr = regime
    ? `Dominant: ${regime.label} (${regime.confidence}% confidence)
  Scores — MR:${regime.scores.meanReversion} BO:${regime.scores.breakout} TR:${regime.scores.trend} NT:${regime.scores.noTrade}
  Posture: ${regime.posture}${modifierLine}
  VWAP dist: ${regime.vwapDist.toFixed(2)}% | RVOL: ${regime.rvol.toFixed(2)}x
  Top drivers: ${regime.topDrivers.slice(0,3).map(d => `${d.key}(${d.label})`).join(', ')}`
    : 'No regime data';

  const schemaStr = `{
  "symbol": "${symbol}",
  "overallBias": "bullish|bearish|neutral|mixed",  // mixed = conflicting directional evidence ONLY; low RVOL alone never justifies mixed when regime+trend+SMC agree
  "marketState": "trend|range|transition|high_noise",
  "conviction": <0-100>,       // reflects certainty of direction — reduce for low participation, do NOT change direction
  "actionability": <0-100>,
  "timeHorizon": "next_15m|next_30m|next_60m",  // match TRADER HORIZON and the setup's expected resolution time; scalp=15m, intraday tighter setup=30m, looser setup=60m
  "primaryThesis": "<2-3 sentence core read — specific, price-level based>",
  "thesisChange": "new|strengthened|weakened|unchanged|invalidated",  // see THESIS CHANGE CLASSIFICATION rules above; reflects actionability trajectory, not signal warmth
  "whatChanged": ["<event 1>", "<event 2>"],  // at least one entry must directly justify thesisChange (setup demotion/advance, gate flip, actionability delta); signal-only moves go here too but cannot alone justify "strengthened"
  "keyDrivers": ["<top driver 1>", "<top driver 2>", "<top driver 3>"],  // the three signals most responsible for the current bias — name engines/levels, not generic phrases
  "liquidityRead": "<where liquidity is resting and what it means>",
  "regimeRead": "<regime interpretation and execution implication>",
  "trapRead": "<active trap risks or 'None identified'>",
  "smcRead": "<SMC structural read — bias, key level, sweep/reversal context>",
  "gammaRead": "<gamma/options context or 'No options data'>",
  "institutionalFlowRead": "<institutional flow interpretation -- accumulation/distribution context, agreement or contradiction with structural bias, or 'No flow data'>",
  "confluenceRead": "<deterministic confluence interpretation -- which engines agree, which dissent, and how that shapes conviction; reference the score and any contradictions explicitly>",
  "bestSetupType": "breakout_long|breakout_short|reversion_long|reversion_short|continuation_long|continuation_short|sweep_reversal_long|sweep_reversal_short|wait",  // map to PRIMARY SETUP block's Type field; use "wait" when setupActionable is FALSE
  "entryLogic": "<specific entry condition with price level>",
  "confirmationNeeded": "<what must happen before entering>",
  "invalidation": "<specific price or event that kills this thesis>",
  "targetLogic": "<first target with reasoning>",
  "primaryRisk": "<the one scenario that makes this entire read wrong>",
  "uncertainty": "<what you cannot know from this data>",
  "shouldTradeNow": <true|false>,
  "urgency": "low|medium|high",
  "modelId": "claude-sonnet-4-6",
  "inputHash": "${ctx.inputHash}"
}`;

  // Scanner context — injected when scanner ran today for this symbol
  const scanCtx = getScannerContext(symbol);
  const scannerSection = scanCtx
    ? `SCANNER PRE-MARKET CONTEXT (MER v4.7 PRO — use as directional prior)
Score: ${scanCtx.score.toFixed(1)}/5.0 | Rating: ${scanCtx.rating} | Conviction: ${scanCtx.conviction}
Setup type: ${scanCtx.setup_type} | Catalyst: ${scanCtx.key_catalyst}
Price target bias: ${scanCtx.price_target_bias} | Universe: ${scanCtx.universe}
This is a daily/fundamental signal. Confirm or contradict it with intraday signals above.`
    : '';

  return `You are an expert intraday trading analyst for Maldonado Equity Research (MER).
You reason with institutional-grade market structure awareness.
You are analyzing ${symbol} in real time.

CURRENT TIME (use this as your "now" reference for ALL time-based reasoning,
especially thesisExpiry.expiresAt — do NOT use any other date from your training data):
${new Date().toISOString()}

TRADER HORIZON: ${horizon.toUpperCase()}
${HORIZON_DIRECTIVES[horizon]}

SESSION CONTEXT
Time of day: ${PHASE_DESC[sessionPhase]}
Market open: ${ctx.isMarketOpen}
${scannerSection ? scannerSection + '\n\n' : ''}

CURRENT PRICE STATE
Price: ${r.price.toFixed(2)} | Session change: ${r.priceChgPct.toFixed(2)}%
VWAP: ${r.vwap.toFixed(2)} (${r.vwapDistPct >= 0 ? '+' : ''}${r.vwapDistPct.toFixed(2)}% ${r.vwapDistPct >= 0 ? 'above' : 'below'})
OR High: ${r.orHigh.toFixed(2)} | OR Low: ${r.orLow.toFixed(2)}
Session High: ${r.sessionHigh.toFixed(2)} | Session Low: ${r.sessionLow.toFixed(2)}
Prior Day High: ${r.prevHigh?.toFixed(2) || 'unknown'} | Prior Day Low: ${r.prevLow?.toFixed(2) || 'unknown'}
RVOL: ${r.rvol.toFixed(2)}x | ${r.candleCount} candles @ ${r.interval}
Last candle: O:${r.open.toFixed(2)} H:${r.high.toFixed(2)} L:${r.low.toFixed(2)} C:${r.price.toFixed(2)} V:${r.volume.toLocaleString()}

REGIME ENGINE
${regimeStr}

SMART MONEY CONCEPTS
${smcStr}

GAMMA / OPTIONS POSITIONING
${gammaStr}${volContextLine}

INSTITUTIONAL FLOW (off-exchange / dark pool / TRF prints, last 5min window)
${flowStr}

LIQUIDITY ZONES (nearest 5 high-significance; [DP] = dark-pool prints cluster within tolerance, confirming the level)
${zoneStr}

TRAP DETECTION
${trapStr}

DETERMINISTIC CONFLUENCE (signed agreement across regime/SMC/gamma/flow/traps)
${confluenceStr}

${buildPrimarySetupBlock(topSetups)}

DETERMINISTIC SETUPS (top 3 by confidence -- for context)
${setupStr}

DETERMINISTIC GATE
setupActionable: ${ctx.setupActionable ? 'TRUE' : 'FALSE'}
${!ctx.setupActionable ? '⚠ CONSTRAINT: The deterministic engine has NOT confirmed setup actionability.\nshouldTradeNow MUST be false. actionability MUST be ≤40. Do not claim the trade is ready.\nState what confirmation is still required based on confirmationMissing fields above.' : 'Setup is confirmed actionable — normal conviction rules apply.'}

PRIOR ANALYSIS
${formatPriorThesis(priorThesis, priorConviction, priorActionability ?? null, priorSetupType ?? null, priorShouldTrade ?? null)}

${priorThesis ? `THESIS CHANGE CLASSIFICATION

Choose exactly one of: new | strengthened | weakened | unchanged | invalidated.

The label appears on the trader's dashboard as a quick-glance indicator
(↑ Strengthened, ↓ Weakened, etc.). Traders make decisions based on it,
so the label must reflect ACTIONABILITY TRAJECTORY — how close the
trade is to executable — not pure signal quality or narrative warmth.

Definitions:

- "new" — no prior thesis exists for this symbol, OR the prior thesis
  was older than the current session.

- "invalidated" — the prior thesis's invalidation level was breached,
  OR the macro overallBias has flipped (bullish↔bearish). This is the
  only label that overrides direction continuity.

- "strengthened" — same overallBias AND the trade got more actionable.
  REQUIRES at least one of:
    (a) actionability rose by ≥10 points vs prior, AND prior direction
        matches current
    (b) deterministic gate moved from FALSE → TRUE (setupActionable
        flipped on)
    (c) the primary setup matured (e.g. "Developing" → confirmed, or a
        setup with no numerical entry gained a specified entry trigger
        within 0.5% of current price)

  Signal-quality improvements alone (e.g. flow flipped to accumulation,
  confluence score rose) are NOT sufficient for "strengthened" if
  actionability stayed flat. Capture those in whatChanged and
  confluenceRead instead. The label is reserved for trajectory toward
  execution, not for warmer narrative tone.

- "weakened" — same overallBias BUT the trade got less actionable.
  Fires on ANY of:
    (a) actionability fell by ≥10 points vs prior
    (b) deterministic gate moved from TRUE → FALSE
    (c) the primary setup demoted (e.g. confirmed → developing, or
        specified entry price → "await trigger" with no numerical entry)
    (d) a High-severity trap appeared that contradicts the setup
        direction

- "unchanged" — same overallBias, actionability within ±9 points of
  prior, same primary setup family, no gate flip. Use this when there
  are real signal moves but no actionability trajectory.

Tie-breaker when multiple rules apply: invalidated > weakened >
strengthened > new > unchanged. (Downside changes dominate. Better
to under-claim improvement than to over-claim it — a trader who sees
"strengthened" expects to act soon.)

Carry the rationale into the whatChanged array: at least one entry
must directly justify the chosen label. If you classify "weakened",
one whatChanged entry must describe the actionability/setup demotion.
If "strengthened", one whatChanged entry must describe the
actionability/setup advance. If "unchanged", whatChanged can describe
signal-level moves without trajectory implications.
` : ''}
YOUR TASK
Synthesize all signals above into a complete AI trading thesis.
Be specific about price levels — never say "near support" without giving the level.
Do NOT restate signals verbatim — reason about what they mean together.
Be honest about conflicting signals. Do not force a bias.

CRITICAL RULE — SEPARATE DIRECTION FROM CONVICTION

Direction and conviction are NOT the same thing. Treat them independently.

If regime, trend, and SMC align directionally:
- ALWAYS preserve that direction in overallBias (bullish or bearish)
- EVEN when RVOL or participation is weak

Low volume / weak participation MUST:
- reduce conviction (e.g. 25–45 range)
- reduce actionability (e.g. 10–35 range)
- reduce urgency (use "low")
- add honest uncertainty language

Low volume / weak participation MUST NOT:
- convert a directional thesis into "mixed"

Use "mixed" ONLY when directional evidence genuinely conflicts, such as:
- bullish trend but bearish SMC reversal
- bearish regime but bullish displacement/reclaim
- structure, displacement, and trend materially disagree

Use "neutral" ONLY when there is no meaningful directional edge at all.

Do NOT use "mixed" or "neutral" simply because conviction is low or volume is light.

Examples of correct behavior:
- bearish regime + bearish trend + bearish SMC + low RVOL:
    overallBias = "bearish", conviction = 25–40, actionability = 10–30, urgency = "low", bestSetupType = "wait"
- bullish regime + bullish trend + bullish SMC + low RVOL:
    overallBias = "bullish", conviction = 25–40, actionability = 10–30, urgency = "low", bestSetupType = "wait"
- bullish trend + bearish SMC reversal active:
    overallBias = "mixed" (genuine conflict — correct use)
- no regime edge + flat trend + no SMC bias:
    overallBias = "neutral" (no edge — correct use)

CRITICAL RULES
- conviction 80+ requires: regime confidence >70%, clear SMC bias, RVOL >1.2, no major trap
- actionability 80+ requires: specific entry trigger within 0.5% of current price
- shouldTradeNow: true only if entry trigger is actively forming or just printed
- Never set urgency "high" unless actionability >70 and confirmation is imminent
- If market is in high_noise regime, set shouldTradeNow: false and urgency: low
- THESIS EXPIRY — You MUST include a "thesisExpiry" object with two fields:
    expiresAt: ISO 8601 timestamp when this thesis becomes stale. CRITICAL — compute this as CURRENT TIME (shown at top of prompt) PLUS the appropriate sizing duration below. Do NOT pick a date from your training data. The result MUST be in the future relative to CURRENT TIME.
    invalidationConditions: array of human-readable predicates (e.g. ["close above 522.64", "RVOL drops below 0.5x"])
  Sizing guidance: scalp = 5-30 min, intraday = 30 min - end of session, swing = 1-3 trading days, position = 5-20 trading days. The trader sets a reminder for expiresAt and walks away — do NOT propose a thesis that requires continuous monitoring.

Return ONLY the JSON below — no markdown fences, no preamble, no explanation:
${schemaStr}`;
}
